import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import type { GorgiasResponse } from '@/features/Setting/Integrations/integrationRedirection/gorgias';
import { GORGIAS_ACCOUNT_SEARCH_PARAM } from '@/features/Setting/Integrations/integrationRedirection/gorgias-redirect';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { GorgiasInfo } from '../types';
import { GorgiasConnectModal } from './GorgiasConnectModal';
import { useGorgias } from './useGorgias';

export const GorgiasItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );
  const [searchParams] = useSearchParams();
  const gorgiasAccount = searchParams.get(GORGIAS_ACCOUNT_SEARCH_PARAM);

  const {
    gorgiasWorkspace,
    disconnectGorgiasMutation,
    requestImportMutation,
    refetch,
  } = useGorgias(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<GorgiasResponse | null>('gorgias-redirect', null);

  const connectModalState = useModalState<string | null>(null);
  const importModalState = useModalState<{
    workspace?: GorgiasInfo;
    isNew: boolean;
  }>();

  useEffect(() => {
    if (!gorgiasAccount) return;
    connectModalState.open({ modalState: gorgiasAccount });
  }, [connectModalState, gorgiasAccount]);

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    refetch();
    fetchFeedbackSources(currWorkspace.alias);
    importModalState.open({ modalState: { isNew: true } });
  }, [
    currWorkspace,
    fetchFeedbackSources,
    importModalState,
    isConnector,
    redirect,
    refetch,
    removeKey,
    setRedirect,
  ]);

  const handleDisconnect = () => {
    disconnectGorgiasMutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  if (isConnector && gorgiasWorkspace) return null;

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!gorgiasWorkspace}
        source={SOURCE_TYPE.GORGIAS}
        description="Import tickets as feedback into Syncly."
        connectedDisplay={gorgiasWorkspace?.name || 'Gorgias'}
        info={gorgiasWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace: GorgiasInfo) => (
          <div>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
        useBackgroundClose
      >
        {gorgiasWorkspace && (
          <IntegrationImportModal
            isNew={importModalState.modalState?.isNew || false}
            title={'Import Tickets'}
            subTitle={'Gorgias'}
            subTitleList={gorgiasWorkspace.name}
            historyList={gorgiasWorkspace.history || []}
            onSubmitImport={(start_date, end_date) =>
              handleImportWorkspace({ start_date, end_date })
            }
          />
        )}
      </Modal>
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
        useBackgroundClose
      >
        <GorgiasConnectModal initialSubdomain={connectModalState.modalState || undefined} />
      </Modal>
    </>
  );
};
